import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useReveal } from "../hooks/useReveal";
import { SECOND_HERO_IMAGE } from "../data/content";

const VALUES = [
    {
        n: "01",
        t: "Small on purpose",
        d: "We take on a handful of homes a year. It's the only way each one can be treated like it's the only one.",
    },
    {
        n: "02",
        t: "Drawn, not decorated",
        d: "Every wardrobe, every ceiling, every corner is drawn in-house before it's built. Nothing left to a supplier.",
    },
    {
        n: "03",
        t: "Honest materials",
        d: "Wood is wood. Stone is stone. Linen is linen. If it's pretending, we won't specify it.",
    },
    {
        n: "04",
        t: "Time as a courtesy",
        d: "We agree on a date, and we hand you the keys on that date. Delay is our currency, not yours.",
    },
];

const About = () => {
    const ref = useReveal();
    return (
        <div ref={ref} data-testid="about-page" className="pt-24 md:pt-32">
            {/* Intro */}
            <section className="max-w-[1440px] mx-auto px-6 md:px-12 py-16 md:py-24">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-end">
                    <div className="lg:col-span-8 reveal">
                        <p className="eyebrow mb-6">About Samora Spaces</p>
                        <h1
                            className="font-display text-samora-ink leading-[0.95] tracking-tight"
                            style={{ fontSize: "clamp(2.75rem, 7vw, 7rem)" }}
                        >
                            A boutique studio<br />
                            <span className="italic font-serif text-samora-gold">
                                for modern Indian homes.
                            </span>
                        </h1>
                    </div>
                    <p className="lg:col-span-4 text-samora-text/75 text-lg leading-relaxed reveal">
                        Samora Spaces was founded in Bengaluru by Sam — a
                        designer who believes a home is the most important
                        thing you'll ever commission, and should be treated
                        that way.
                    </p>
                </div>
            </section>

            {/* Big image */}
            <section className="reveal">
                <img
                    src={SECOND_HERO_IMAGE}
                    alt="A warm, moody living room by Samora Spaces"
                    className="w-full h-[60vh] md:h-[80vh] object-cover"
                />
            </section>

            {/* Founder note */}
            <section className="py-24 md:py-40 bg-samora-sand/40">
                <div className="max-w-[1440px] mx-auto px-6 md:px-12">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
                        <div className="lg:col-span-5 reveal">
                            <img
                                src="/team/sam.png"
                                alt="Sam — Founder & Principal Designer, Samora Spaces"
                                className="w-full h-[540px] md:h-[640px] object-cover object-top"
                            />
                            <p className="mt-4 text-xs tracking-widest uppercase text-samora-mute">
                                Sam · Founder & Principal Designer
                            </p>
                        </div>
                        <div className="lg:col-span-7 lg:pl-8 reveal">
                            <p className="eyebrow mb-6">A note from Sam</p>
                            <h2
                                className="font-display text-samora-ink leading-[1.05] tracking-tight"
                                style={{
                                    fontSize: "clamp(2rem, 4.5vw, 4rem)",
                                }}
                            >
                                "I started Samora because most Indian homes are
                                built <span className="italic font-serif text-samora-gold">for a catalogue</span>, not
                                for the family living in them."
                            </h2>
                            <div className="mt-8 space-y-5 text-samora-text/75 text-base md:text-lg leading-relaxed max-w-2xl">
                                <p>
                                    Before Samora, I spent a decade working
                                    with architects and design-and-build firms
                                    across India. I saw the same story again
                                    and again: beautiful renders, weak
                                    execution, and homes that started aging on
                                    day one.
                                </p>
                                <p>
                                    Samora Spaces is my answer to that. A tiny
                                    studio. A small book of projects each
                                    year. Solid timber. Real stone. Honest
                                    drawings. And a promise: I'll be on your
                                    site more than you will.
                                </p>
                                <p>
                                    If that sounds like the studio you've been
                                    waiting for — let's talk.
                                </p>
                            </div>
                            <Link
                                to="/contact"
                                data-testid="about-cta-write"
                                className="btn-samora mt-10"
                            >
                                Write to Sam <ArrowRight size={16} />
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* Values */}
            <section className="py-24 md:py-40 bg-samora-bone">
                <div className="max-w-[1440px] mx-auto px-6 md:px-12">
                    <div className="max-w-3xl mb-16 md:mb-24 reveal">
                        <p className="eyebrow mb-6">What We Believe</p>
                        <h2
                            className="font-display text-samora-ink leading-[0.95] tracking-tight"
                            style={{ fontSize: "clamp(2.25rem, 5vw, 4.5rem)" }}
                        >
                            Four small<br />
                            <span className="italic font-serif text-samora-gold">
                                studio principles.
                            </span>
                        </h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16">
                        {VALUES.map((v) => (
                            <div
                                key={v.n}
                                data-testid={`value-${v.n}`}
                                className="reveal border-t border-samora-ink/20 pt-8"
                            >
                                <span className="font-serif text-samora-gold text-lg">
                                    — {v.n}
                                </span>
                                <h3 className="font-display text-3xl md:text-4xl text-samora-ink mt-3">
                                    {v.t}
                                </h3>
                                <p className="mt-4 text-samora-text/70 text-base leading-relaxed max-w-md">
                                    {v.d}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Stats */}
            <section className="py-24 md:py-32 bg-samora-ink text-samora-bone">
                <div className="max-w-[1440px] mx-auto px-6 md:px-12">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-y-14">
                        {[
                            { n: "60+", l: "Homes Delivered" },
                            { n: "12", l: "Cities" },
                            { n: "5.0", l: "Google Rating" },
                            { n: "100%", l: "On-Time Handover" },
                        ].map((s) => (
                            <div
                                key={s.l}
                                data-testid={`stat-${s.l.toLowerCase().replace(/ /g, "-")}`}
                                className="text-center reveal"
                            >
                                <p
                                    className="font-display text-samora-gold"
                                    style={{
                                        fontSize: "clamp(3rem, 6vw, 6rem)",
                                        lineHeight: 1,
                                    }}
                                >
                                    {s.n}
                                </p>
                                <p className="mt-3 eyebrow text-samora-bone/70">
                                    {s.l}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
};

export default About;
